import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Outlet } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import {
  huddleHeartbeat,
  joinHuddle,
  leaveHuddle,
  myLiveHuddles,
  huddlesAvailable,
  recordHuddleChunk,
  type LiveHuddle,
} from './huddleApi';
import { HuddleMesh, type PeerView } from './mesh';
import { HuddleRecorder, saveThenLeave, type RecorderCtor } from './recorder';
import { SIGNAL_EVENT, parseSignal } from './signalling';
import { TurnKeeper, fetchTurn, iceServersFor } from './turn';
import { UploadQueue } from './uploadQueue';
import { INITIAL_CALL, callReducer, isActive, type CallState } from './useHuddle';

/**
 * The call lives in the signed-in shell, not in a page (h1-plan.md:216): going
 * from Messages to My Day must not drop it. HuddleLayout wraps the TAP guards.
 *
 * Everything that talks to the browser or the server is here; every decision
 * is in useHuddle.ts (reducer), mesh.ts, recorder.ts and uploadQueue.ts.
 */

export const HEARTBEAT_MS = 15_000;
export const OPEN_ROOM_POLL_MS = 10_000;
/**
 * The room list's own cadence (messages/hooks.ts ROOM_POLL_INTERVAL_MS, 60 s).
 * Kept here, not imported: several Messages tests mock that module whole.
 */
export const LIVE_POLL_MS = 60_000;
export const PIECES_BUCKET = 'huddle-pieces';

export const huddleKeys = {
  live: () => ['huddles', 'live'] as const,
};

export interface HuddleCall {
  /** Server permission to offer new calls. Existing-call controls remain usable. */
  available: boolean;
  state: CallState;
  peers: PeerView[];
  join: (roomId: string, roomName: string) => void;
  leave: () => void;
  toggleMute: () => void;
  dismiss: () => void;
}

const INERT: HuddleCall = {
  available: false,
  state: INITIAL_CALL,
  peers: [],
  join: () => undefined,
  leave: () => undefined,
  toggleMute: () => undefined,
  dismiss: () => undefined,
};

const HuddleContext = createContext<HuddleCall>(INERT);

/** Exported for tests that render the bar with a stub call. */
export const HuddleCallContext = HuddleContext;

export function useHuddleCall(): HuddleCall {
  return useContext(HuddleContext);
}

/**
 * Rooms with a live huddle, for the room dot and the Start/Join button.
 * Polled at the rooms' cadence (60 s), every 10 s when the caller asks (the
 * open room while not joined, h1-plan.md:226). An error (the database has no
 * huddles yet, or the rpc refused) reads as "no huddles": the page hides the
 * button rather than offering a call it cannot place.
 */
export function useLiveHuddles(fast = false) {
  const { client, access } = useAuth();
  // Outside a provider (Messages tests, supplier shell) there is no call, so
  // no rpc either: the context default is inert.
  const { available } = useContext(HuddleContext);
  return useQuery({
    queryKey: huddleKeys.live(),
    queryFn: async () => {
      const rows = await myLiveHuddles(client);
      return new Map<string, LiveHuddle>(rows.map((row) => [row.projectId, row]));
    },
    enabled: available && access.status === 'approved' && access.profile.account_type === 'tap',
    refetchInterval: fast ? OPEN_ROOM_POLL_MS : LIVE_POLL_MS,
    retry: false,
  });
}

interface Session {
  huddleId: string;
  participantId: string;
  stream: MediaStream;
  mesh: HuddleMesh | null;
  channel: { unsubscribe: () => unknown } | null;
  recorder: HuddleRecorder | null;
  queue: UploadQueue | null;
  heartbeat: ReturnType<typeof setInterval> | null;
  turn: TurnKeeper | null;
  closed: boolean;
}

function micErrorKind(cause: unknown): 'denied' | 'missing' | 'other' {
  const name =
    typeof cause === 'object' && cause !== null ? (cause as { name?: unknown }).name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'missing';
  return 'other';
}

function stopStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop();
}

export function HuddleProvider({ children }: { children: ReactNode }) {
  const { client, access } = useAuth();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(callReducer, INITIAL_CALL);
  const [peers, setPeers] = useState<PeerView[]>([]);
  const session = useRef<Session | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const me =
    access.status === 'approved' && access.profile.account_type === 'tap'
      ? {
          userId: access.profile.id,
          name: access.profile.display_name.trim() || access.profile.email,
        }
      : null;

  const actorId = me?.userId ?? null;
  const availability = useQuery({
    queryKey: ['huddles', 'available', me?.userId ?? ''],
    queryFn: () => huddlesAvailable(client),
    enabled: me !== null,
    retry: false,
    refetchInterval: LIVE_POLL_MS,
  });
  const available = me !== null && availability.data === true && !availability.isError;
  const generation = useRef(0);
  const joining = useRef(false);

  const refreshLive = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: huddleKeys.live() });
  }, [queryClient]);

  /** Close everything this tab holds. The seat itself is left by the caller or the sweep. */
  const teardown = useCallback(
    (sayBye: boolean) => {
      const current = session.current;
      if (current === null || current.closed) return;
      current.closed = true;
      if (current.heartbeat !== null) clearInterval(current.heartbeat);
      current.turn?.stop();
      current.queue?.stop();
      void current.recorder?.stop();
      current.mesh?.close(sayBye);
      if (current.channel !== null) void client.removeChannel(current.channel as never);
      stopStream(current.stream);
      setPeers([]);
      session.current = null;
      refreshLive();
    },
    [client, refreshLive],
  );

  const join = useCallback(
    (roomId: string, roomName: string) => {
      if (!available || me === null || joining.current || isActive(stateRef.current.phase)) return;
      joining.current = true;
      const ownGeneration = generation.current;
      dispatch({ type: 'join_start', roomId, roomName });
      void (async () => {
        // 1. The mic first, so a refused mic never holds one of the 12 seats.
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
        } catch (cause) {
          joining.current = false;
          if (ownGeneration !== generation.current) return;
          const kind = micErrorKind(cause);
          dispatch(
            kind === 'denied'
              ? { type: 'mic_denied' }
              : kind === 'missing'
                ? { type: 'mic_missing' }
                : { type: 'join_failed', message: 'The microphone could not be opened.' },
          );
          return;
        }

        if (ownGeneration !== generation.current) {
          stopStream(stream);
          return;
        }
        // 2. The seat. The relay credentials are asked for at the same time
        //    (one request per call, shared by every peer, turn.ts); they are
        //    awaited just before the first peer is made, in step 4.
        const turnGrant = fetchTurn(client);
        const sessionId = crypto.randomUUID();
        let joined;
        try {
          joined = await joinHuddle(client, roomId, sessionId);
        } catch (cause) {
          joining.current = false;
          stopStream(stream);
          if (ownGeneration !== generation.current) return;
          dispatch({ type: 'join_failed', message: cause instanceof Error ? cause.message : '' });
          refreshLive();
          return;
        }
        joining.current = false;
        if (ownGeneration !== generation.current) {
          stopStream(stream);
          void leaveHuddle(client, joined.participantId).catch(() => undefined);
          return;
        }
        const t0 = performance.now();
        const current: Session = {
          huddleId: joined.huddleId,
          participantId: joined.participantId,
          stream,
          mesh: null,
          channel: null,
          recorder: null,
          queue: null,
          heartbeat: null,
          turn: null,
          closed: false,
        };
        session.current = current;
        dispatch({
          type: 'joined',
          huddleId: joined.huddleId,
          participantId: joined.participantId,
          max: joined.max,
          joinedAt: Date.now(),
        });
        refreshLive();

        // 3. The recorder and its queue (plan 2.1: same stream, starts once
        //    the channel is subscribed).
        const queue = new UploadQueue({
          huddleId: joined.huddleId,
          participantId: joined.participantId,
          upload: async (path, blob, contentType) => {
            const { error } = await client.storage
              .from(PIECES_BUCKET)
              .upload(path, blob, { contentType, upsert: false });
            return { error };
          },
          record: (chunk) => recordHuddleChunk(client, chunk),
          refreshSession: async () => {
            const { error } = await client.auth.refreshSession();
            return error === null;
          },
          now: () => performance.now(),
          sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
          onState: (next) => {
            if (next === 'not_recorded') current.recorder?.refused();
            dispatch({ type: 'recording', state: next });
          },
        });
        const recorder = new HuddleRecorder({
          stream,
          Recorder: (globalThis as { MediaRecorder?: RecorderCtor }).MediaRecorder,
          sink: queue,
          now: () => performance.now(),
          clockBaseMs: joined.clockBaseMs,
          t0,
          onState: (next) => dispatch({ type: 'recording', state: next }),
        });
        current.queue = queue;
        current.recorder = recorder;

        // 4. The mesh on the private channel huddle:<id>. The relay first:
        //    fetchTurn never throws, and null means STUN only (T5), so a
        //    missing relay never stops the call from starting.
        const grant = await turnGrant;
        if (current.closed) return;
        if (grant === null) dispatch({ type: 'relay_unavailable' });
        let refetching = false;
        const refetchRoster = () => {
          if (refetching || current.closed) return;
          refetching = true;
          huddleHeartbeat(client, joined.participantId)
            .then((roster) => current.mesh?.setRoster(roster))
            .catch((cause: unknown) => {
              if (cause instanceof Error && cause.message.includes('not_in_huddle')) {
                dispatch({ type: 'seat_lost' });
                teardown(false);
              }
            })
            .finally(() => {
              refetching = false;
            });
        };
        const channel = client.channel(`huddle:${joined.huddleId}`, {
          config: { private: true, broadcast: { self: false, ack: false } },
        });
        const mesh = new HuddleMesh({
          me: { userId: me.userId, sessionId },
          name: me.name,
          stream,
          send: (signal) => {
            void channel.send({ type: 'broadcast', event: SIGNAL_EVENT, payload: signal });
          },
          onChange: () => setPeers(mesh.views()),
          onUnknownSender: refetchRoster,
          iceServers: iceServersFor(grant),
          env: {
            createPeer: (config) => new RTCPeerConnection(config),
            createAudio: () => new Audio(),
            setTimeout: (fn, ms) => setTimeout(fn, ms),
            clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
          },
        });
        current.mesh = mesh;
        current.channel = channel;
        mesh.setRoster(joined.peers);

        // Past expiresAt minus 10 minutes: ask again, give every open peer
        // the new servers (T4). A STUN-only call has nothing to renew.
        const turn = new TurnKeeper({
          fetch: () => fetchTurn(client),
          apply: (servers) => mesh.setIceServers(servers),
          now: () => Date.now(),
          setTimeout: (fn, ms) => setTimeout(fn, ms),
          clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
        });
        current.turn = turn;
        turn.start(grant);

        channel
          .on('broadcast', { event: SIGNAL_EVENT }, (message: { payload?: unknown }) => {
            const signal = parseSignal(message.payload);
            if (signal !== null) void mesh.handle(signal).catch(() => undefined);
          })
          .subscribe((status) => {
            if (current.closed) return;
            if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
              mesh.hello();
              recorder.start();
            } else if (
              status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR ||
              status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT
            ) {
              // The seat is left to the 60 s sweep (h1-plan.md:236).
              dispatch({ type: 'channel_lost' });
              teardown(false);
            }
          });

        // 5. The heartbeat keeps the seat and refreshes the roster.
        current.heartbeat = setInterval(refetchRoster, HEARTBEAT_MS);
      })();
    },
    [client, me, available, refreshLive, teardown],
  );

  const leave = useCallback(() => {
    const current = session.current;
    if (current === null || stateRef.current.phase !== 'in_call') return;
    dispatch({ type: 'leave_start' });
    void saveThenLeave({
      recorder: current.recorder,
      queue: current.queue,
      leave: async () => {
        await leaveHuddle(client, current.participantId).catch(() => undefined);
      },
      sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    }).finally(() => {
      teardown(true);
      dispatch({ type: 'left' });
    });
  }, [client, teardown]);

  const toggleMute = useCallback(() => {
    const current = session.current;
    if (current === null) return;
    const muted = !stateRef.current.muted;
    current.mesh?.setMuted(muted);
    current.recorder?.setMuted(muted);
    dispatch({ type: 'muted', muted });
  }, []);

  const dismiss = useCallback(() => dispatch({ type: 'dismiss' }), []);

  // Tab close: a bye (best effort, the sweep covers crashes) and a prompt
  // only while audio is still waiting to upload (plan 2.7).
  useEffect(() => {
    const onPageHide = () => {
      session.current?.mesh?.close(true);
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if ((session.current?.queue?.size ?? 0) > 0) {
        event.preventDefault();
      }
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  // Signing out or unmounting the shell ends this tab's part in the call.
  useEffect(() => {
    dispatch({ type: 'left' });
    return () => {
      generation.current += 1;
      joining.current = false;
      teardown(true);
    };
  }, [actorId, teardown]);

  const value = useMemo<HuddleCall>(
    () => ({ available, state, peers, join, leave, toggleMute, dismiss }),
    [available, state, peers, join, leave, toggleMute, dismiss],
  );
  return <HuddleContext.Provider value={value}>{children}</HuddleContext.Provider>;
}

/**
 * The route layout that holds the call. It wraps the three TAP route branches
 * in App.tsx (RequireTap, RequireTapAdmin, RequireSuperAdmin), each of which
 * mounts its own TapLayout. With the provider inside TapLayout, going to
 * /admin unmounted it and ended the call (Dev4 lane 2 review, R3). Up here it
 * stays mounted across all three; leaving them (sign out, /login) ends it.
 */
export function HuddleLayout() {
  return (
    <HuddleProvider>
      <Outlet />
    </HuddleProvider>
  );
}
